import type {
  ProviderDirectoryV2ContactPoint,
  ProviderDirectoryV2SourceMode,
  ProviderDirectoryV2SourceType,
} from '@/services/types/provider-directory-v2-types';

export interface ContactPointProvenanceDraft {
  sourceMode: ProviderDirectoryV2SourceMode;
  sourceType: ProviderDirectoryV2SourceType;
  sourceRef: string;
  note: string;
}

export const SOURCE_TYPE_LABELS: Record<ProviderDirectoryV2SourceType, string> = {
  CALLED_PROVIDER: 'Called provider',
  PROVIDER_WEBSITE: 'Provider website',
  DOCUMENT: 'Document',
  OTHER: 'Other',
};

export const DEFAULT_PROVENANCE_DRAFT: ContactPointProvenanceDraft = {
  sourceMode: 'MANUAL',
  sourceType: 'CALLED_PROVIDER',
  sourceRef: '',
  note: '',
};

export const toProvenanceDraft = (
  point: ProviderDirectoryV2ContactPoint,
): ContactPointProvenanceDraft => ({
  sourceMode: point.source_mode ?? 'SYSTEM',
  sourceType: point.source_type ?? 'CALLED_PROVIDER',
  sourceRef: point.source_ref ?? '',
  note: point.note ?? '',
});

export const validateProvenanceDraft = (
  draft: ContactPointProvenanceDraft,
): string | null => {
  if (draft.sourceMode === 'SYSTEM') {
    return null;
  }

  if (!draft.sourceType) {
    return 'Source type is required for manual contacts.';
  }

  if (
    (draft.sourceType === 'PROVIDER_WEBSITE' ||
      draft.sourceType === 'DOCUMENT') &&
    !draft.sourceRef.trim()
  ) {
    return 'Source reference is required for website/document contacts.';
  }

  if (draft.sourceType === 'OTHER' && !draft.note.trim()) {
    return 'A note is required when source type is Other.';
  }

  return null;
};

export const buildProvenancePayload = (draft: ContactPointProvenanceDraft) => {
  if (draft.sourceMode === 'SYSTEM') {
    return {
      source_mode: 'SYSTEM' as const,
    };
  }

  return {
    source_mode: 'MANUAL' as const,
    source_type: draft.sourceType,
    source_ref: draft.sourceRef.trim() || null,
    note: draft.note.trim() || null,
  };
};

const firstString = (value: unknown): string | null => {
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    return typeof first === 'string' ? first : null;
  }
  if (typeof value === 'string') return value;
  return null;
};

export interface DuplicateContactPointError {
  existingContactPointId: string;
  fieldMessage: string;
}

export const parseDuplicateContactPointError = (
  error: unknown,
): DuplicateContactPointError | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const {data} = (error as { data?: unknown });
  if (!data || typeof data !== 'object') {
    return null;
  }

  const existingId = firstString(
    (data as { existing_contact_point_id?: unknown }).existing_contact_point_id,
  );
  if (!existingId) {
    return null;
  }

  const valueMessage = firstString((data as { value?: unknown }).value);
  const addressMessage = firstString(
    (data as { address_line1?: unknown }).address_line1,
  );

  return {
    existingContactPointId: existingId,
    fieldMessage:
      valueMessage ??
      addressMessage ??
      'An active contact point with this value already exists.',
  };
};
